import { useEffect, useState } from 'react';

interface PositionState {
  coords: [number, number] | null;
  isLoading: boolean;
  error: Error | null;
}

// London Bridge, because why not?
export const DEFAULT_POSITION: [number, number] = [51.50846879254079, -0.08768680882971101];

export const usePosition = ({ autoFetch = true, defaultCoords = DEFAULT_POSITION }: {
  autoFetch?: boolean,
  defaultCoords?: [number, number],
}) => {
  const [state, setState] = useState<PositionState>({
    coords: null,
    isLoading: false,
    error: null,
  });

  const getPosition = async () => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: new Error('Geolocation is not supported by your browser'),
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      setState({
        coords: [latitude, longitude],
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error getting location:', error);
      setState(prev => ({
        ...prev,
        coords: defaultCoords,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Failed to get location'),
      }));
    }
  };

  useEffect(() => {
    if (autoFetch) {
      getPosition();
    }
  }, [autoFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    coords: state.coords,
    isLoading: state.isLoading,
    error: state.error,
    getPosition,
  };
};
